<template>
  <section class="player" :class="{'player--is-player-only': isPlayerOnly}">
    <div class="player__wrapper player__wrapper--video" :class="{'is-sticky': isSticky}" ref="wrapper">
      <div class="player__container" :class="{'pre-roll': this.isPreRoll}">
        <div class="adBanner" ref="adbanner"></div>
        <div v-if="!adBlocked" id="the-player" ref="playercontainer"></div>
        <div v-else class="player__container--ad-blocking-message">
          This video is unavailable because we were unable to load a message from our sponsors. <br><br>If you are using ad-blocking software, please disable it and reload the page.
        </div>
        <div v-if="playerHasOverlay" class="player__overlay" @click="overlayClickHandler" :style="{ backgroundImage: 'url(' + overlayImage + ')' }" />
        <div v-if="playerIsStopped" class="player__stopped" @click="restartLiveFeed" :style="{ backgroundImage: 'url(' + stoppedImage + ')' }">
          <div class="cvui-large-play">
            <button class="cvui-button-icon cvui-play-btn"></button>
          </div>
        </div>
        <div v-if="isSticky" class="player__header">
          <h1>{{itemTitle}}</h1>
          <button @click="stickyCloseHandler" class="player__close">
            <SVGLoader :src="'close-x-alt.svg'"></SVGLoader>
          </button>
        </div>
      </div>
    </div>
    <div v-if="!isPlayerOnly" class="player__wrapper player__wrapper--title">
      <h1 v-if="!isMoreInfoAvailable" class="player__title player__title--now" :class="{'is-live': resources.isLive}">{{ itemTitle }}</h1>
      <MoreInfo
        v-else
        v-bind:current="resources.current"/>
      <h4 class="player__title player__title--next">{{ nextTitle }}</h4>
      <SocialShare v-bind:current="resources.current"/>
    </div>
  </section>
</template>

<script>
/* global myVideoManager:true, myVideoPlayer:true, drupalSettings, vtg, cbsoptanon */
import { mapState, mapGetters, mapMutations } from 'vuex'
import settings from '../assets/configs/settings'
import embedConfigs from '../assets/configs/embed'
import utagData from '../assets/configs/utag'
import helpers from '../store/store-helpers'
import utils from '../assets/utils'
import adUtils from '../assets/utils/ads'

export default {
  name: 'AviaPlayer',
  components: { MoreInfo: () => import(/* webpackChunkName: "moreInfo" */ '../components/MoreInfo'), SocialShare: () => import(/* webpackChunkName: "socialShare", webpackPrefetch: true */ '../components/SocialShare'), SVGLoader: () => import(/* webpackChunkName: "svgLoader", webpackPrefetch: true */ '../components/SVGLoader') },
  mounted () {
    this.checkForRedirects()

    // create unique sessionId
    this.sessionId = utils.randomAlphaNumberic(40)

    // check utag data
    if (typeof (window.utag_data) === 'undefined') {
      this.setWindowUtagData()
    }
    // backup settings if no response from settings API
    if (this.configSettings === null) {
      this.setConfigSettings(settings)
    }
    this.playTime = this.contentTimeForAds
    this.liveAssetId = this.configSettings.acquisition.liveStreamGUID

    this.setPlayerPreviewImageSize()

    if (this.drupalSettings.enableFMS) {
      if (window.fmscw) {
        window.fmscw.retrieve({
          app: {
            coppa: 0
          }
        }, {
          errorResponseTTL: 3600,
          timeout: 3000
        })
        .then(fmsResponse => {
          this.fmsResponse = fmsResponse
          this.loadPlayer()
        })
        .catch(error => {
          console.error(error);
          // Generate a random ID for fw_vcid2 parameter in case window.fmscw fails.
          this.fmsResponse = {
            fms_params: {
              imafw__fw_vcid2: Math.floor((Math.random() * 1000000) + 1)
            }
          }
          this.loadPlayer()
        })
      }
    }
    else {
      this.loadPlayer()
    }

    if (this.$refs.adbanner.clientHeight <= 0) {
      this.adBlocked = true
    }

    // Onetrust gate
    if (typeof cbsoptanon !== 'undefined') {
      cbsoptanon.ot.getState((targeting, performance, functional, social) => {
        this.oneTrust = { targeting, performance, functional, social }
      })
    }
  },
  async beforeDestroy () {
    // Remove the player on unmount
    if(myVideoPlayer) {
      this.unlistenToPlayerEvents();
      await avia.removeVideoPlayer(myVideoPlayer.id);
    }
  },
  computed: {
    ...mapState([
      'isPlaying',
      'isAdPlaying',
      'isPlayerReady',
      'isPlayerOnly',
      'resources',
      'contentTimeForAds',
      'breakpoint',
      'configSettings',
      'daiParams',
      'mediaAutoplayCustom',
      'isPreRoll',
      'default_preview_image',
      'default_stopped_image',
      'dotqProjectId',
      'dotqPixelId',
      'dotqet',
      'dotqec',
      'dotqea',
      'partner',
      'startingRoute',
      'currentRoute',
      'playerHasOverlay',
      'playerIsStopped',
      'mutedAutoplayStreamID',
      'maxAttempts',
      'recreationAttempts',
      'failureContainerId',
      'continuousPlay'
    ]),
    ...mapGetters([
      'wasVideoOrVODPage',
      'isLivePage',
      'isLiveEnabled',
      'isDVRPage',
      'isVODPage',
      'isLiveVideo',
      'getRouteNameByPath',
      'playlists'
    ]),
    itemTitle () {
      return this.resources.current.StoryHead
    },
    nextTitle () {
      if (this.resources.current.Category !== 'Latest') {
        return this.resources.next !== null ? this.resources.next.StoryHead : this.resources.live.StoryHead
      } else {
        return this.resources.current.UpNext
      }
    },
    isMoreInfoAvailable () {
      return (this.resources.current.Description !== '' && typeof this.resources.current.Description !== 'undefined')
    },
    unmuteButtonText () {
      let bp = this.breakpoint
      if (bp === 'xs' || bp === 'sm') {
        return 'TAP TO UNMUTE'
      }
      return 'CLICK TO UNMUTE'
    },
    overlayImage () {
      return this.default_preview_image + this.playerPreviewImageSize
    },
    stoppedImage () {
      return this.default_stopped_image + this.playerPreviewImageSize
    }
  },
  methods: {
    ...mapMutations([
      'setConfigSettings',
      'setIsPlaying',
      'setIsAdPlaying',
      'addToEndpoints',
      'setMediaAutoplayCustom',
      'setIsPreRoll',
      'setIsPlayerReady',
      'setPlayerHasOverlay',
      'setPlayerIsStopped',
      'setMaxAttempts',
      'setRecreationAttempts',
      'setFailureContainerId',
      'setContinuousPlay'
    ]),
    /**
     * Adds some redirect logic
     */
    checkForRedirects () {
      // set up a redirect if path + slug does not match url
      if (this.resources.current.StoryPath + this.resources.current.StorySlug !== this.$route.path) {
        if (this.resources.isLive) {
          if (this.$route.path === '/live') return
          this.$router.push({ name: (this.$route.path === '/embed/live' ? 'embed-live' : 'live-stream'), query: this.$route.query }).catch((exception)=>{ console.log(exception) })
        } else {
          this.$router.push({
            name: this.getRouteNameByPath(this.$router.options.routes, this.resources.current.StoryPath),
            params: {
              slug: this.resources.current.StorySlug,
              tray: this.resources.tray
            }
          })
        }
      }

      // redirect to /live if route is not /live or /embed/live and currently playing live stream
      if (this.isLiveVideo(this.resources.current) && (this.$route.path !== '/live' && this.$route.path !== '/embed/live')) {
        history.replaceState({}, 'Live', '/live')
      }
    },
    /**
     * Update window.utag_data from utag config file
     */
    setWindowUtagData () {
      window.utag_data = utagData
    },
    /**
     * Update player preview image size
     */
    setPlayerPreviewImageSize () {
      let bp = this.breakpoint
      if (bp === 'xs') {
        this.playerPreviewImageSize = '?height=212&width=375'
      } else if (bp === 'sm' || bp === 'md') {
        this.playerPreviewImageSize = '?height=434&width=768'
      } else {
        this.playerPreviewImageSize = '?height=576&width=1024'
      }
    },
    /**
     * Reinit the player
     */
    async reInitPlayer() {
      const container = '#the-player'

      if(myVideoPlayer) {
        this.unlistenToPlayerEvents();
      }
      await avia.removeVideoPlayer(container);
      //await this.loadPlayer();
    },
    /**
     * Restart live feed
     */
    restartLiveFeed() {
      this.setPlayerIsStopped(false);
      this.loadPlayer();
    },
    /**
     * Loads the AVIA player.
     */
    loadPlayer () {
      this.isMuted = false
      this.setDisplayConfig(this.resources.current)

      // this.isPlayerReady is to check if the player already initialized once
      // the muted autoplay overlay should only show once per session
      if (this.resources.isLive && !this.isPlayerReady) {
        this.setPlayerHasOverlay(true)
      }

      try {
        const isAnalyticsVerified = true

        // Create Video Player
         avia.createVideoPlayer({
          container: `#the-player`,
          id: this.failureContainerId || '#the-player',
          playsInline: true,
          autoplay: avia.Autoplay.ATTEMPT_UNMUTED_THEN_MUTED,
          uiConfig: {
            posterUrl: this.default_preview_image + this.playerPreviewImageSize,
            displayUnmutePromptAtPlayStart: !!avia.Autoplay.ATTEMPT_UNMUTED_THEN_MUTED,
            unmutePromptStyles: 'top: 15px'
          },
          plugins: [{
            name: 'Tracking',
            classRef: isAnalyticsVerified
              ? vtg.ts.Cvp
              : null,
            options: { config: vtg.config },
            priority: avia.PluginPriority.HIGH
          }]
        }).then((p) => {
           this.onVideoPlayerReady(p)
        })
      }
      catch (error) {
        // Force player recreation under a new container ID
        if(this.recreationAttempts < this.maxAttempts) {
          try {
            this.setRecreationAttempts(this.recreationAttempts + 1)
            this.setFailureContainerId(`#the-player-reinit-${ this.recreationAttempts }`)
          }
          catch(error) {
            console.log('CVP Container Error, Attempting Recreation')
            this.reInitPlayer()
          }
        }
        console.error(error);
      }
    },

    setDisplayConfig() {
      let device = 'default'
      if(utils.isMobile()) {
        device = 'mobile'
      }

      this.displayMode.autoplay = false
      this.displayMode.muted = false

      if(window.drupalSettings.hasOwnProperty('playerConfig')) {
        if (this.resources.isLive) {
          this.displayMode = window.drupalSettings.playerConfig.live[device]
        } else {
          this.displayMode = window.drupalSettings.playerConfig.vod[device]
        }
      }
    },

    /**
     * Listen to Video Player event
     */
    listenToPlayerEvents() {
      const events = avia.PlayerEvent;

      Object.keys(events).forEach(k => {
        myVideoPlayer.on(events[k], this.handlePlayerEvent);
      });
    },

    /**
     * Unlisten to Video Player event
     */
    unlistenToPlayerEvents() {
      const events = avia.PlayerEvent;

      Object.keys(events).forEach(k => {
        myVideoPlayer.off(events[k], this.handlePlayerEvent);
      });
    },

    /**
     * Loads and plays a resource.
     */
    loadVideo (resource, isAnalyticsVerified) {
      this.randomStr = this.randomize(1, 999999999)
      this.removeResourceEndEvent()
      let newResource

      if (!myVideoPlayer || typeof this.playlists === 'undefined') {
        return false
      }

      if (this.resources.isLive === true && !this.playerHasOverlay) {
        try {
          // Create DAI Resource
          newResource = this.prepareDAIResourceObject(resource)
        } catch (exception) {
          console.log(exception)
        }
      } else {
        // Create Regular Resource
        newResource = this.prepareResourceObject(resource)
      }

      // combine resource metadata with window utag_data
      const final = {...newResource.metadata, ...window.utag_data}

      newResource.metadata = isAnalyticsVerified
        ? {...final, ...(om.getHbParams && om.getHbParams())}
        : final

      // Assign resource to player
      myVideoPlayer.resource = newResource
      this.addResourceEndEvent()
    },

    /**
     * Video Player Init Handler
     */
    onVideoPlayerInit (e) {
      // This is important: Keep this reference to the player so you have it
      // when the user clicks another video (image)
      myVideoPlayer = e.target.player
    },

    /**
     * Video Player Ready handler
     */
    onVideoPlayerReady (videoPlayer) {
      myVideoPlayer = videoPlayer

      if(myVideoPlayer) {
        // Allow captions to play correctly on desktop.
        if (!navigator.userAgent.match(/(iPad|iPhone|iPod)/g) || !navigator.vendor.match(/(Apple Computer)/g)) {
          //  let vidContainerId = myVideoPlayer.getVidContId()
          //  let videoEl = document.getElementById(vidContainerId).querySelector('video')
          let videoEl = document.getElementById('the-player').querySelector('video')
          videoEl.setAttribute('crossorigin', 'anonymous')
        }

        // Assign Player Events
        this.listenToPlayerEvents()

        this.loadVideo(this.resources.current)

        this.addWindowScrollEvent()
        this.addWindowResizeEvent()

        this.setIsPlayerReady(true)
      }
    },

    handlePlayerEvent (e) {
      const types = avia.PlayerEvent

      switch (e.type) {
        // error handling
        case types.ERROR:
        case types.RESOURCE_ERROR:
        case types.VIDEO_START_ERROR:
        case types.VIDEO_PLAYBACK_ERROR:
          console.error(`CVP Has experienced an error with code ${e.code} and message "${ e.message }"`);
          break;
        case types.AD_ERROR:
        case types.AD_BLOCKED:
          this.adBlocked = true
          console.error(`CVP Has experienced an AD error ${e.code} and message "${ e.message }"`);
          break;

        // player
        case types.PLAYER_INIT:
          this.onVideoPlayerInit(e)
          break
        case types.LIVE_PRESENTATION_STOPPED:
          this.onLivePresentationStopped()
          break

        // ads
        case types.AD_START:
          this.onAdPodStart()
          break
        case types.AD_POD_END:
          this.onAdPodEnd(e)
          break

        // resource and content
        case types.RESOURCE_START:
          this.onResourceStart()
          break
        case types.CONTENT_PLAYING:
        case types.CONTENT_START:
          this.onContentStart(e)
          break
        case types.RESOURCE_INTERRUPTED:
          this.handleVideoStateChange(e)
          this.setContinuousPlay(0)
          break
        case types.RESOURCE_END:
          this.onResourceEnd()
          this.setContinuousPlay(1)
          break

        // control
        case types.CONTROL_UNMUTE:
          this.handleControlUnmute()
          break

        default:
          // Fall through
          this.adBlocked = false
      }
    },

    /**
     * Control Unmute event handler
     */
    handleControlUnmute () {
      this.isMuted = false
    },

    /**
     * RESOURCE_START event handler
     */
    onResourceStart () {
      window.scrollTo(0, 0)
      this.isSticky = false
    },

    /**
     * RESOURCE_END event handler
     */
    onResourceEnd () {
      if (this.resources.next !== null && !this.resources.next.Hidden) {
        this.$router.push({
          name: this.getRouteNameByPath(this.$router.options.routes, this.resources.next.StoryPath),
          params: {
            slug: this.resources.next.StorySlug,
            tray: this.resources.tray
          }
        })
      } else {
        if (!this.isLiveEnabled) {
          this.$router.push({name: 'video'})
        } else {
          this.$router.push({name: (this.$route.path === '/embed/live' ? 'embed-live' : 'live-stream')}).catch((exception)=>{ console.log(exception) })
        }
      }
      this.setMediaAutoplayCustom(true)
    },

    /**
     * VIDEO_STATE_CHANGE event handler
     */
    handleVideoStateChange (e) {
      if (e.target.playbackState === avia.PlaybackState.PLAYING && !this.isPlaying) {
        this.setIsPlaying(true)
        this.initiateOnPlayEvents()
        this.startAdTimer()
      } else if (this.isPlaying) {
        this.setIsPlaying(false)
        this.stopAdTimer()
      }
    },

    /**
     * Adds dotq to window object
     */
    initiateOnPlayEvents () {
      window.dotq = window.dotq || []
      window.dotq.push(
        {
          'projectId': this.dotqProjectId,
          'properties': {
            'pixelId': this.dotqPixelId,
            'qstrings': {
              'et': this.dotqet,
              'ea': this.dotqea,
              'ec': this.dotqec
            }
          } })
    },

    /**
     * Click Handler for sticky player's close button
     */
    stickyCloseHandler () {
      this.disableSticky = true
      this.isSticky = false
    },

    /**
     * Mute Button Handler
     */
    unmuteClickHandler () {
      this.isMuted = false
    },

    /**
     * Mute Button Handler
     */
    overlayClickHandler () {
      this.setPlayerHasOverlay(false)
      this.loadVideo(this.resources.current)
    },

    /**
     * Cross-browser check for fullscreen
     */
    isFullScreen() {
      return !!(document.fullscreen || document.webkitIsFullScreen || document.mozFullScreen || document.msFullscreenElement || document.fullscreenElement);
    },

    /**
     * LIVE_PRESENTATION_STOPPED
     */
    onLivePresentationStopped() {
      if (this.getMediaContentType() === 'live') {
        if (this.isFullScreen()) {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else {
            document.msExitFullScreen();
          }
        }
        this.reInitPlayer();
        this.setPlayerIsStopped(true);
      }
    },

    /**
     * AD_POD_START event handler
     */
    onAdPodStart () {
      if (!this.resources.isLive && utils.isMobile()) {
        this.setIsPreRoll(true)
      }
      this.setIsAdPlaying(true)
      this.adBlocked = false
    },

    /**
     * AD_POD_END event handler
     */
    onAdPodEnd (e) {
      if (e.target.adPod.hasPlayed) {
        this.playTime = 0
      }
      this.setIsPreRoll(false)
      this.setIsAdPlaying(false)
    },

    /**
     * Playlist Recommendation Handler
     */
    onPlaylistRecommendation (e) {
      var pl = e.payload.playlist
    },

    /**
     * CONTENT_START event handler
     */
    onContentStart (e) {
    },

    /**
     * Adds the RESOURCE_END event listener to the video player.
     */
    addResourceEndEvent () {
      myVideoPlayer.on(avia.PlayerEvent.RESOURCE_END, this.handlePlayerEvent)
    },

    /**
     * Removes the RESOURCE_END event listener to the video player.
     */
    removeResourceEndEvent () {
      myVideoPlayer.off(avia.PlayerEvent.RESOURCE_END, this.handlePlayerEvent)
    },

    /**
     * Adds a scroll event listener to the window object.
     */
    addWindowScrollEvent () {
      window.addEventListener('scroll', this.stickyHandler)
    },

    /**
     * Adds a resize event listener to the window object.
     */
    addWindowResizeEvent () {
      this.stickyThreshold = this.$refs.wrapper.offsetTop + (this.$refs.wrapper.offsetHeight / 2)
      if (utils.isIOS()) {
        window.addEventListener('orientationchange', this.stickyHandler)
        window.addEventListener('orientationchange', this.updateStickyThreshold)
      } else {
        window.addEventListener('resize', this.stickyHandler)
        window.addEventListener('resize', this.updateStickyThreshold)
      }
    },

    /**
     * Scroll and resize handler for sticky player - toggles sticky player on and off.
     */
    stickyHandler (e) {
      let topPos

      if (e.target.scrollingElement) {
        topPos = e.target.scrollingElement.scrollTop
      } else {
        topPos = window.pageYOffset
      }

      if (!this.disableSticky) {
        if ((this.stickyThreshold <= topPos) && !this.isSticky) {
          this.isSticky = true
        } else if ((this.stickyThreshold > topPos) && this.isSticky) {
          this.isSticky = false
        }
      }
      if ((this.stickyThreshold > topPos) && this.disableSticky) {
        this.disableSticky = false
      }
    },

    /**
     * Changes threshold depending on device orientation
     */
    updateStickyThreshold () {
      import("../assets/utils").then(utils => {
        utils.default.debounce(this, () => {
          this.stickyThreshold = this.$refs.wrapper.offsetTop + (this.$refs.wrapper.offsetHeight / 2)
        }, 100)
      })
    },

    /** HELPER FUNCTIONS **/
    /**
     * Click to view live video
     *
     * @returns {string}
     */
    prepareAdCallUrl (resource, network="dfp", id3 = false, payload = null) {
      let resultAdCall = ''
      let customParams = null
      let npa = null

      // Check for Onetrust Acceptance and set NPA flag if enabled.
      if(typeof cbsoptanon !== 'undefined') {
        cbsoptanon.ads.getNpaFlag(function(flag) {
          npa = flag !== 0 ? flag : 0
        })
      }

      if(!resource.hasPreRoll) {
        return null
      }

      switch(network) {
        case 'dfp':
        case 'ima':
          if(this.resources.isLive || resource.hasPreRoll) {
            let platformId = 8264
            let departmentNamespace = 'vaw-ctd'
            let deviceType = !utils.isMobile() ? 'desktop' : 'mobile_web'
            let playerBrand = !utils.isMobile() ? 'etonline' : 'etonline_mobile' + (this.$route.path === '/embed/live' ? '_' + this.partner : '')
            let ppValue = 'vpaid_js'
            let segmentation_upid = '0100'
            if (payload) {
              if (payload.hasOwnProperty('segmentation_upid') && payload.segmentation_upid.length) {
                segmentation_upid = '0' + payload.segmentation_upid[0]
              }
            }

            // Don't strip categories. Return true.
            let tags = resource.AdTags.filter(function (tag) {
              return true
            })

            tags = encodeURIComponent(tags)
            let adCall = ''

            let cmsID = window.drupalSettings.playerConfig.cmsid ? window.drupalSettings.playerConfig.cmsid : '2289'
            let vID = resource.ReferenceId ? resource.ReferenceId.toString() : resource.StoryId.toString()

            if (this.isLivePage) {
              vID = this.configSettings.mpxDvrID
            }
            if (this.resources.isLive) {
              vID = this.configSettings.mpxLiveID
            }

            if (adUtils.isVod()) {
              let vguidValue = adUtils.videoPlayerGetValueFromPage('vguid_value')
              let ftagValue = adUtils.videoPlayerGetValueFromPage('ftag_value')
              adCall = 'https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/' +
                      platformId + '/' +
                      departmentNamespace + '/' +
                      deviceType + '/' +
                      playerBrand +
                      '&ad_rule=1&impl=s&gdfp_req=1&env=vp&output=xml_vast4&unviewed_position_start=1&url=' +
                      document.referrer +
                      '&video_url_to_fetch=' +
                      window.location.origin + window.location.pathname + encodeURIComponent(window.location.search) +
                      '&vid=' +
                      vID +
                      '&cust_params=site%3Dctd%26show%3D' +
                      playerBrand +
                      '%26kw%3D' +
                      tags +
                      '%26ptype%3D' +
                      'video' +
                      '%26section%3Dvideo' +
                      '%26vguid%3D' +
                      vguidValue +
                      '%26ftag%3D' +
                      ftagValue +
                      '%26feat%3D' +
                      segmentation_upid +
                      '&cmsid=' +
                      cmsID +
                      '&npa=' +
                      npa +
                      '&pp=' +
                      ppValue +
                      '&description_url=https://etlive.etonline.com/live/' +
                      '&url=https://etlive.etonline.com/live/' +
                      '&session=&subses=&bidid=' +
                      '&correlator=' + this.randomStr +
                      '&scor=' + this.randomStr
            } else if (this.resources.isLive) { // live stream
              let podDuation = (payload !== null && typeof (payload.duration) !== 'undefined') ? payload.duration : 90000
              adCall = 'https://pubads.g.doubleclick.net/gampad/live/ads?sz=640x480&iu=/' +
                      platformId + '/' +
                      departmentNamespace + '/' +
                      deviceType + '/' +
                      playerBrand +
                      '&correlator=' + this.randomStr +
                      '&scor=' + this.randomStr +
                      '&ad_rule=0' +
                      '&impl=s&gdfp_req=1&env=vp&output=xml_vast4&unviewed_position_start=1' +
                      '&url=https://etlive.etonline.com/live' +
                      '&description_url=https://etlive.etonline.com/live' +
                      '&cmsid=' +
                      cmsID +
                      '&vid=' + vID +
                      '&vad_type=linear&vpos=midroll&pmad=50&pmnd=0&pmxd=' + podDuation + '&pp=vpaid_js&cust_params=feat%3D' + segmentation_upid
            } else {
              adCall = 'https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/' +
                      platformId + '/' +
                      departmentNamespace + '/' +
                      deviceType + '/' +
                      playerBrand +
                      '&ad_rule=1' +
                      '&impl=s&gdfp_req=1&env=vp&output=xml_vast4&unviewed_position_start=1' +
                      '&cust_params=vid%3D' + vID +
                      '%26feat%3D' +
                      segmentation_upid +
                      '&cmsid=' +
                      cmsID +
                      '&description_url=https://etlive.etonline.com/live/' +
                      '&url=https://etlive.etonline.com/live/' +
                      '&session=&subses=&bidid=' +
                      '&vid=' + vID +
                      '&correlator=' + this.randomStr +
                      '&scor=' + this.randomStr
            }

            if (((this.playTime >= this.contentTimeForAds || this.wasVideoOrVODPage) && !this.resources.isLive) || (id3 && !utils.isMobile())) {
              resultAdCall = adCall
            }
          }
          break
        case 'freewheel':
          let adurl = new URL(this.drupalSettings.fw_base_url)
          let fwNetworkId = this.drupalSettings.fw_network_id
          let fwBrand = this.drupalSettings.fw_brand
          let caid = resource.ReferenceId
          let description_url = this.drupalSettings.fw_desc_url
          let keywords = this.formatAdTagsToKeywords(resource.AdTags, 'video')

          // Global Params
          adurl.searchParams.append('asnw', fwNetworkId)
          adurl.searchParams.append('caid', caid)
          adurl.searchParams.append('csid', `vcbs_${ fwBrand }_${ utils.isMobile() ? 'mobile' : 'desktop' }_web_${ this.$route.path === '/embed/live' ? 'vod_embed' : 'vod'}`)
          adurl.searchParams.append('flag', '+amcb+emcr+sltp+slcb+qtcb+vicb+fbad+sync+nucr+aeti')
          adurl.searchParams.append('metr', 1023)
          adurl.searchParams.append('nw', fwNetworkId)
          adurl.searchParams.append('prof', `${ fwNetworkId }:GoogleDAICSAI_v01`)
          adurl.searchParams.append('pvrn', `${this.randomize(1000000000000000, 9000000000000000)}`)
          adurl.searchParams.append('resp', 'vmap1+vast4')
          adurl.searchParams.append('ssnw', fwNetworkId)

          // Key Values
          adurl.searchParams.append('vprn', `${this.randomize(1000000000000000, 9000000000000000)};${ this.resources.isLive ? 'imafw__fw_continuous_play' : '_fw_continuous_play' }=${ this.continuousPlay || 0 }`)
          adurl.searchParams.append('_fw_h_referer', window.location.href)
          adurl.searchParams.append('_fw_is_lat', npa)
          adurl.searchParams.append('npa', npa)


          // Custom Params
          adurl.searchParams.append('cpPre', 0)
          adurl.searchParams.append('cpSession', this.continuousPlay || 0)
          adurl.searchParams.append('description_url', description_url)
          adurl.searchParams.append('embed', 0)
          adurl.searchParams.append('partner', fwBrand)
          adurl.searchParams.append('section', window.utag_data.siteSection)
          adurl.searchParams.append('show', fwBrand)
          adurl.searchParams.append('site', 'ctd')
          adurl.searchParams.append('sz', '640x480')
          if (keywords) {
            adurl.searchParams.append('keywords', keywords)
          }
          adurl.searchParams.append('ptype', window.utag_data.pageType)
          adurl.searchParams.append('vguid', window.utag_data.pageViewGuid)
          adurl.searchParams.append('ftag', window.utag_data.campaign ? window.utag_data.campaign : false)
          // FMS Values
          if (this.fmsResponse && this.fmsResponse.fms_params) {
            for (const [k, v] of Object.entries(this.fmsResponse.fms_params)) {
              adurl.searchParams.append(k, v)
            }
          }
          const regex = /imafw_/g
          adurl.href = adurl.href.replace(regex, '').replace('__fw_vcid2', '_fw_vcid2')
          resultAdCall = adurl ? adurl.href.replace('%3B', ';').replace('%2C%2C', '%2C').replaceAll('%26', '&').replaceAll('%3D', '=') : null
          break
        default:
          console.log(`The selected ad network "${ network }" is not supported.`)
      }

      return resultAdCall
    },

    /**
     * Converts timecode to seconds
     *
     * @returns {int}
     */
    convertToSeconds (hms) {
      let t = hms.split(':')
      let seconds = (+t[0]) * 60 * 60 + (+t[1]) * 60 + (+t[2])
      return seconds
    },

    /**
     * Starts preroll ad's minimum playtime timer
     */
    startAdTimer () {
      clearInterval(this.timer)
      this.timer = setInterval(() => {
        if (!this.isAdPlaying) {
          this.playTime += 1
        }
      }, 1000)
    },

    /**
     * Stops preroll ad's minimum playtime timer
     */
    stopAdTimer () {
      clearInterval(this.timer)
    },

    /**
     * This is to determine a value for tracking
     *
     * @returns {string}
     */
    getMediaContentType () {
      if (this.isDVRPage) {
        return 'dvr'
      } else if (this.isVODPage) {
        return 'vod'
      } else {
        return 'live'
      }
    },

    /**
     * Generates a random string
     *
     * @returns {string}
     */
    randomize (min, max) {
      return Math.floor((Math.random() * (max - min) + min)).toString()
    },

    /**
     * Get video index in its playlist
     *
     * @returns {string}
     */
    getVideoIndex (resource) {
      const currentPlaylist = this.playlists.filter(pl => pl.category === this.resources.tray)
      if (currentPlaylist && currentPlaylist.length > 0 && currentPlaylist[0].items)
        return currentPlaylist[0].items.findIndex(rco => rco.StorySlug === resource.StorySlug)
      else
        return 0
    },
    /**
     * Get assetId
     *
     * @returns {string}
     */
    getAssetId (resource) {
      let assetId = resource.StoryId.toString() // default to story id
      if (this.resources.isLive) {
        // live resource
        if (this.playerHasOverlay) {
          assetId = this.mutedAutoplayStreamID
        }
        else {
          assetId = this.liveAssetId
        }
      }
      if (this.getMediaContentType() === 'vod' && resource.ReferenceId) {
        // vod uses reference id
        assetId = resource.ReferenceId
      }

      return assetId
    },
    /**
     * Format AdTags to be keywords
     *
     * @returns {string}
     */

    formatAdTagsToKeywords (adTags, mType) {
      if (!adTags || adTags.length === 0) {
        return null
      }

      let keywords = adTags.join().toLowerCase().replaceAll('&', 'and').replace(/ /g, '_').replace(/,$/, '').replaceAll(/,+/g, ',').split(',')
      if (keywords && keywords.length > 0) {
        if (mType === 'video') {
          return keywords.join('&keywords=')
        }
        if (mType === 'live') {
          return 'keywords=' + keywords.join('&keywords=')
        }
      }
      return ''
    },

    /**
     * Prepares context metadata object
     *
     * @returns {object}
     */
    prepareExtraMetadata (resource) {
      let metadata = {
        videoTitle: resource.StoryHead.toString(),
        assetId: this.getAssetId(resource),
        mediaAutoplayCustom: this.mediaAutoplayCustom.toString(),
        sitePrimaryRsid: 'cbsietonlinesite',
        brandPlatformId: window.utag_data.brandPlatformId,
        siteType: window.utag_data.siteType,
        deviceType: window.utag_data.deviceType,
        siteSection: 'live',
        mediaRegion: this.resources.isLive ? 'live' : resource.Category.toLowerCase(),
        assetType: this.resources.isLive ? 'live' : 'vod',
        mediaContentType: this.getMediaContentType(),
        streamType: this.resources.isLive ? 'live' : 'vod',
        assetTitle: this.resources.isLive ? (this.playerHasOverlay ? 'ET Live muted' : 'ET Live') : resource.StoryHead,
        siteHier: window.utag_data.siteHier,
        pageType: window.utag_data.pageType,
        pageViewGuid: window.utag_data.pageViewGuid,
        assetPubDate: (new Date(resource.StartTime * 1000)).toISOString(),
        userState: window.utag_data.userState,
        userType: window.utag_data.userType,
        pageName: 'etonline :' + document.location.pathname,
        pageURL: window.self !== window.top ? document.referrer : document.location.href,
        vidPlayerPos: 'hero',
        'cm.ssf' : this.onetrust.targeting ? '0' : '1'
      }

      return metadata
    },



    /**
     * Prepares resource object for player consumption.
     * @param {object} resource
     * @returns {object}
     */
    prepareResourceObject (resource) {
      const mainRCO = avia.createResourceConfig()
      mainRCO.location.mediaUrl = resource.AssetURL
      mainRCO.mediaId = this.resources.isLive ? 'E' + resource.StoryId.toString() : this.getAssetId(resource)
      mainRCO.metadata = {
        videoIdForTracking: resource.StoryId,
        videoId: resource.StoryId,
        duration: this.resources.isLive ? -1 : this.convertToSeconds(resource.Duration),
        vidPlaylistPos: this.getVideoIndex(resource),
        vidContID: 'the-player',
        category: resource.Category,
        fileType: 'video',
        cmsRefGuid: resource.StoryId,
        isLive: this.resources.isLive,
      }

      // old contextMetadata
      mainRCO.metadata = {...mainRCO.metadata, ...this.prepareExtraMetadata(resource)}

      const network = this.drupalSettings.adProvider
      if(!this.adBlocked && !this.playerHasOverlay) {
        mainRCO.ad.context = avia.AdContext.IMA

        if(network === 'freewheel') {
          mainRCO.ad.csai.adCallUrl = this.prepareAdCallUrl(resource, network, false, null)
        }
        else {
          mainRCO.ad.ima.adCallUrl = this.prepareAdCallUrl(resource, network, false, null)
        }
      }

      // Captions
      if(resource.CaptionsURL) {
        mainRCO.location.timedTextUrl = resource.CaptionsURL
      }
      if (resource.CaptionsURL && (utils.isIE() || navigator.userAgent.match(/Android/i))) {
        mainRCO.closedCaptionPath = resource.CaptionsURL
        mainRCO.closedCaptionType = "text/vtt"
      }

      // add/override for embed page things
      if (this.$route.path === '/embed/live') {
        mainRCO.metadata.assetType = 'video'
        mainRCO.metadata.brandPlatformId = 'etlive-' + this.partner + '_site_' + (utils.isMobile() ? 'mweb' : 'desktop')
        mainRCO.metadata.pageName = this.partner + ':/'
        mainRCO.metadata.pageType = 'embedded'
        mainRCO.metadata.siteSection = this.partner
      }

      return mainRCO
    },

    /**
     * Prepares DAI resource object
     *
     * @returns {object}
     */
    prepareDAIResourceObject (resource, payload = null) {
      let segmentation_upid = '0100'
      if (payload) {
        if (payload.hasOwnProperty('segmentation_upid') && payload.segmentation_upid.length) {
          segmentation_upid = '0' + payload.segmentation_upid[0]
        }
      }

      let myDAILiveRCO = avia.createResourceConfig()
      let vid = this.configSettings.mpxLiveID
      let trackingId = resource.StoryId

      if(resource.EndTime !== 0) {
        trackingId = this.configSettings.mpxLiveID
      }

      if(typeof resource.ReferenceId !== 'undefined') {
        vid = resource.ReferenceId
        trackingId = resource.ReferenceId
      }

      myDAILiveRCO = {
        isLive: this.resources.isLive,
        ad: {
          context: 'dai_sdk'
        },
        metadata: {
          videoIdForTracking: trackingId,
          videoIdForAdCalls: trackingId,
          vidPlaylistPos: '0'
        },
        mediaId: this.resources.isLive ? 'E' + resource.StoryId.toString() : this.getAssetId(resource),
      }

      if(myDAILiveRCO.metadata) {
        myDAILiveRCO.metadata = {...myDAILiveRCO.metadata, ...this.prepareExtraMetadata(resource)}
      }

      myDAILiveRCO.ad.dai = {
        daiVideoId: this.configSettings.mpxLiveID,
        assetKey: this.configSettings.daiKey,
        isLive: true
      }

      myDAILiveRCO.ad.adTagParameters = {
        iu: this.daiParams,
        cust_params: '%26feat%3D'+ segmentation_upid + '%26vguid%3D' + adUtils.videoPlayerGetValueFromPage('vguid_value'),
        sz: '640x480',
        description_url: 'https%3A%2F%2Fwww.etonline.com%2Flive',
        ppid: this.sessionId,
        vid: vid,
        vguid: window.utag_data.pageViewGuid,
        url: window.location.href,
        idtype: 'dpid'
      }

      if (this.drupalSettings.adProvider === 'freewheel') {
        let FWParameters = {
          imafw_csid: `vcbs_${ this.drupalSettings.fw_brand }_${ utils.isMobile() ? 'mobile' : 'desktop' }_web_live`,
          imafw_embed: 0,
          imafw_ftag: window.utag_data.campaign ? window.utag_data.campaign : false,
          imafw_host: window.location.hostname,
          imafw_keywords: this.formatAdTagsToKeywords(resource.AdTags, 'live'),
          imafw_partner: this.drupalSettings.fw_brand ? this.drupalSettings.fw_brand : 'etonline',
          imafw_ptype: window.utag_data.pageType,
          imafw_section: window.utag_data.siteSection,
          imafw_show: this.drupalSettings.fw_brand ? this.drupalSettings.fw_brand : 'etonline',
          imafw_site: 'ctd',
          imafw_sz: '640x480',
          imafw_vguid: window.utag_data.pageViewGuid
        }
        try{
          // FMS Values
          if (this.fmsResponse && this.fmsResponse.fms_params) {
            Object.assign(FWParameters, this.fmsResponse.fms_params)
          }
        } catch(e) {
          console.error(e)
        }

        myDAILiveRCO.ad.ssai = {
          assetKey: this.configSettings.daiKey,
          isLive: true,
          adParameters: Object.assign(myDAILiveRCO.ad.adTagParameters, FWParameters)
        }
      }

      return myDAILiveRCO
    }
  },
  watch: {
    resources (val, oldVal) {
      if (typeof myVideoPlayer !== 'undefined') { // fix this
        if ((!oldVal.isLive || !val.isLive) && this.isPlayerReady) {
          this.loadVideo(val.current)
        }
      }
    }
  },
  data () {
    return {
      infoAvailable: false,
      internalNS: '.etlive',
      playerOptions: {},
      sessionOptions: {
        usesIMA: true,
        partner: 'cbs',
        skin: true,
        autoPlay: false,
        previewImage: this.default_preview_image + this.playerPreviewImageSize,
        stoppedImage: this.default_stopped_image + this.playerPreviewImageSize,
        playsInline: true,
        id3OwnerIds: 'cbsinteractive.etlive, com.cbsi.live.sg'
      },
      isMuted: false,
      onetrust: {},
      currentComscoreType: 'live',
      displayMode: {},
      isSticky: false,
      stickyThreshold: 0,
      adBlocked: false,
      disableSticky: false,
      playerContainer: 'the-player',
      playTime: 0,
      liveAssetId: null,
      timer: null,
      randomStr: this.randomize(1, 9999999),
      playerPreviewImageSize: '?height=576&width=1024',
      sessionId: null,
      drupalSettings: drupalSettings || window.drupalSettings,
      fmsResponse: null,
      fmsCount: 0
    }
  }
}
</script>

<style lang="sass">
  @import '../assets/scss/components/Player.scss'
</style>
